import { formatDeliveryStatus } from '../lib/format';
import type { AttemptStatus, DeliveryStatus } from '../types';

export type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'accent';

export type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  tone: Tone;
  /** Redundant with colour on purpose: status must survive a greyscale screenshot. */
  glyph: string;
  label: string;
  title?: string;
  size?: BadgeSize;
}

export function Badge({ tone, glyph, label, title, size = 'md' }: BadgeProps) {
  return (
    <span className={`badge badge--${tone} badge--${size}`} title={title}>
      <span className="badge__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="badge__label">{label}</span>
    </span>
  );
}

const DELIVERY_TONES: Record<DeliveryStatus, { tone: Tone; glyph: string }> = {
  succeeded: { tone: 'success', glyph: '✓' },
  pending: { tone: 'warning', glyph: '◷' },
  dead_lettered: { tone: 'danger', glyph: '✕' },
};

export function DeliveryStatusBadge({ status, size }: { status: string; size?: BadgeSize }) {
  const config = DELIVERY_TONES[status as DeliveryStatus] ?? { tone: 'neutral' as Tone, glyph: '?' };
  return <Badge tone={config.tone} glyph={config.glyph} label={formatDeliveryStatus(status)} size={size} />;
}

const ATTEMPT_TONES: Record<AttemptStatus, { tone: Tone; glyph: string; label: string }> = {
  succeeded: { tone: 'success', glyph: '✓', label: 'Succeeded' },
  failed: { tone: 'danger', glyph: '✕', label: 'Failed' },
};

export function AttemptStatusBadge({ status, size = 'sm' }: { status: string; size?: BadgeSize }) {
  const config = ATTEMPT_TONES[status as AttemptStatus] ?? {
    tone: 'neutral' as Tone,
    glyph: '?',
    label: status || 'Unknown',
  };
  return <Badge tone={config.tone} glyph={config.glyph} label={config.label} size={size} />;
}
